import { useEffect, useState } from 'react';
import axiosInstance from '../utils/axiosInstance';

export interface NavCounts {
    withdrawals: number;
    orders: number;
}

const EMPTY: NavCounts = { withdrawals: 0, orders: 0 };

/**
 * The count endpoints return whole tables, so navigating between pages would
 * re-download them each time. Cache module-wide and only refetch when stale.
 */
const TTL_MS = 30_000;

let cache: NavCounts | null = null;
let cachedAt = 0;
let inFlight: Promise<NavCounts> | null = null;

/**
 * Bumped by invalidate(). A request whose generation is stale when it lands
 * is discarded, so a fetch started before an action can't overwrite the
 * fresher numbers that action produced.
 */
let generation = 0;

const subscribers = new Set<(c: NavCounts) => void>();

async function load(): Promise<NavCounts> {
    // Collapse concurrent callers onto one request.
    if (inFlight) return inFlight;

    const myGeneration = generation;

    inFlight = (async () => {
        try {
            const [wRes, oRes] = await Promise.all([
                axiosInstance.post('admin/protected/getWithdrawals', {}),
                axiosInstance.post('admin/protected/getOrders', {}),
            ]);

            const withdrawals = (wRes?.data?.data ?? []).filter(
                (w: any) => w?.withdrawalStatus === 'pending'
            ).length;
            const orders = (oRes?.data?.data ?? []).filter(
                (o: any) => o?.orderStatus === 'pending'
            ).length;

            const next = { withdrawals, orders };

            if (myGeneration !== generation) return cache ?? next;

            cache = next;
            cachedAt = Date.now();
            subscribers.forEach((fn) => fn(next));
            return next;
        } catch {
            // A failed count must never break the shell — just show nothing.
            return cache ?? EMPTY;
        } finally {
            inFlight = null;
        }
    })();

    return inFlight;
}

/** Drops the cache and refetches. Call after an action changes a pending count. */
export function invalidateNavCounts(): void {
    cache = null;
    cachedAt = 0;
    generation += 1;
    inFlight = null; // don't ride an in-flight request started before the action
    load();
}

export function useNavCounts(): NavCounts {
    const [counts, setCounts] = useState<NavCounts>(cache ?? EMPTY);

    useEffect(() => {
        subscribers.add(setCounts);

        const fresh = cache && Date.now() - cachedAt < TTL_MS;
        if (fresh) {
            setCounts(cache as NavCounts);
        } else {
            load().then(setCounts);
        }

        return () => {
            subscribers.delete(setCounts);
        };
    }, []);

    return counts;
}
